import type { MaybeRef } from 'vue'
import type {
  ID,
  MetaElement,
  Response,
  StaticPagesMetaElement,
  StaticPagesMetaElementForm,
  StoreThis,
} from '@/types'
import type { ToastState } from '@/types/ui'
import type { MetaModelTypes } from '@/types/entites'

import { ref } from 'vue'
import { v4 as uuidv4 } from 'uuid'

import { defineStore } from 'pinia'
import { useUiStore } from '@/stores/ui'

import { clone, handleErrors, unProxy } from '@/helpers'

const resource = 'metas'

export const useMetasStore = defineStore(resource, () => {
  const ui = useUiStore()

  const staticPages = ref<StaticPagesMetaElementForm[]>([])
  const staticPagesAreLoading = ref(false)

  const metaModelIsLoading = ref(false)

  async function fetchStaticPages(this: StoreThis) {
    staticPagesAreLoading.value = true

    try {
      const {
        data: { data, success, errors },
      }: Response<StaticPagesMetaElement[]> = await this.rest.get(
        `/m/${resource}`
      )

      if (!success && errors) throw Error(handleErrors(errors))

      staticPages.value = data?.map((page) => prepareElementForm(page)) ?? []
    } catch (e) {
      console.error('[fetchStaticPages]', resource, e)
    } finally {
      staticPagesAreLoading.value = false
    }
  }

  async function editStaticPages(
    this: StoreThis,
    form: StaticPagesMetaElementForm[]
  ) {
    const options: Partial<ToastState> = {}
    let error = false
    let otherError = ''
    let validationErrors = {}

    const payload = form.map((page) => prepareElement(page))

    staticPagesAreLoading.value = true

    try {
      const {
        data: { success, errors, data },
      }: Response<StaticPagesMetaElement[]> = await this.rest.post(
        `/m/${resource}`,
        clone(unProxy(payload))
      )

      if (!success && errors) {
        validationErrors = errors.validation ?? {}
        otherError = errors.other ?? ''

        throw Error(handleErrors(errors))
      }

      options.type = 'success'
      options.text = 'Изменения сохранены'

      staticPages.value = data?.map((page) => prepareElementForm(page)) ?? []
    } catch (e) {
      error = true
      console.error('[editStaticPages]', resource, e)

      options.text = 'Что-то пошло не так. Сохранить не удалось'
    } finally {
      staticPagesAreLoading.value = false

      if (options.text) ui.showToast(options)
    }

    return {
      error,
      otherError,
      validationErrors,
    }
  }

  async function editMetaModel(
    this: StoreThis,
    model: MetaModelTypes,
    id: ID,
    payload: MaybeRef<MetaElement>,
    hideSuccess?: boolean
  ) {
    const options: Partial<ToastState> = {}
    let error = false
    let otherError = ''
    let validationErrors = {}
    let response = null

    metaModelIsLoading.value = true

    try {
      const {
        data: { success, errors, data },
      }: Response<MetaElement> = await this.rest.put(
        `/m/${resource}/${model}/${id}`,
        clone(unProxy(payload))
      )

      if (!success && errors) {
        validationErrors = errors.validation ?? {}
        otherError = errors.other ?? ''

        throw Error(handleErrors(errors))
      }

      response = data

      options.type = 'success'

      if (!hideSuccess) options.text = 'Изменения в SEO сохранены'
    } catch (e) {
      error = true
      console.error('[editMetaModel]', resource, e)

      options.text = 'Что-то пошло не так. Внести изменения в SEO не удалось'
    } finally {
      metaModelIsLoading.value = false

      if (options.text) ui.showToast(options)
    }

    return {
      error,
      model,
      id,
      response,
      otherError,
      validationErrors,
    }
  }

  function resetStaticPages() {
    staticPages.value = []
  }

  function prepareElementForm(
    element: StaticPagesMetaElement
  ): StaticPagesMetaElementForm {
    return { ...element, uuid: uuidv4() }
  }

  function prepareElement(
    elementForm: StaticPagesMetaElementForm
  ): StaticPagesMetaElement {
    const element = clone(elementForm)

    delete element.uuid

    return element
  }
  /**
   * Storefront
   * */
  async function fetchStaticPagesC(this: StoreThis) {
    staticPagesAreLoading.value = true

    try {
      const {
        data: { data, success, errors },
      }: Response<StaticPagesMetaElement[]> = await this.rest.get(
        `/c/${resource}`
      )

      if (!success && errors) throw Error(handleErrors(errors))

      staticPages.value = data?.map((page) => prepareElementForm(page)) ?? []
    } catch (e) {
      console.error('[fetchStaticPagesC]', resource, e)
    } finally {
      staticPagesAreLoading.value = false
    }
  }

  return {
    staticPages,
    staticPagesAreLoading,

    fetchStaticPages,
    editStaticPages,
    resetStaticPages,

    metaModelIsLoading,
    editMetaModel,

    fetchStaticPagesC,
  }
})
